<template>
  <ui-modal-layout :icon="UserRoundIcon">
    <template #header>
      <div v-if="lead" class="flex min-w-0 items-center gap-1.5">
        <h2 class="m-0 truncate text-xl leading-tight font-semibold text-foreground">{{ lead.displayName || tr('Lead') }}</h2>
        <ui-button
          v-if="lead.displayName"
          variant="ghost"
          size="icon-xs"
          :aria-label="tr('Copy to clipboard')"
          @click="() => copy(lead.displayName)"
        >
          <copy-icon />
        </ui-button>
      </div>
      <ui-skeleton v-else class="h-6 w-48" />
      <div v-if="lead" class="flex min-w-0 flex-wrap items-center gap-1.5">
        <ui-badge variant="secondary" class="gap-1">
          <i class="icon meta size-3 bg-current" />
          {{ lead.leadgen_form?.name }}
        </ui-badge>
        <ui-badge v-if="lead.campaign" variant="outline" class="max-w-full">
          <span class="truncate">{{ lead.campaign.name }}</span>
        </ui-badge>
      </div>
    </template>

    <template v-if="lead">
      <div v-if="lead.phone || lead.email" class="flex flex-col gap-3">
        <div v-for="contact in contacts" :key="contact.type" class="glass-tile flex flex-col gap-3 rounded-xl p-4">
          <div class="flex items-center gap-3">
            <span class="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <component :is="contact.icon" class="size-5" />
            </span>
            <div class="flex min-w-0 flex-1 flex-col">
              <span class="text-xs text-muted-foreground">{{ contact.label }}</span>
              <span class="truncate text-base font-medium text-foreground">{{ contact.value }}</span>
            </div>
            <ui-button
              variant="ghost"
              size="icon-sm"
              :aria-label="tr('Copy to clipboard')"
              @click="() => copy(contact.value)"
            >
              <copy-icon />
            </ui-button>
          </div>
          <ui-button :variant="contact.type === 'phone' ? 'default' : 'outline'" as-child>
            <a :href="contact.href">
              <component :is="contact.icon" />
              {{ contact.action }}
            </a>
          </ui-button>
        </div>
      </div>

      <div class="grid grid-cols-2 gap-4 rounded-xl bg-foreground/4 p-4">
        <div class="flex flex-col gap-1.5">
          <span class="text-xs font-semibold tracking-wide text-muted-foreground uppercase">{{ tr('Status') }}</span>
          <lead-status-select
            :model-value="lead.status"
            :disabled="!canManage"
            :loading="savingField === 'status'"
            @update:model-value="(status) => changeStatus(status)"
          />
        </div>
        <div class="flex min-w-0 flex-col gap-1.5">
          <span class="text-xs font-semibold tracking-wide text-muted-foreground uppercase">{{ tr('Assignee') }}</span>
          <lead-assignee-select
            :assignee="lead.assignee"
            :members="companyMemberOptions"
            :disabled="!canManage || userListIsLoading"
            :loading="savingField === 'assignee'"
            @update:assignee="(userId) => changeAssignee(userId)"
          />
        </div>
        <div class="flex flex-col gap-1">
          <span class="text-xs font-semibold tracking-wide text-muted-foreground uppercase">{{ tr('Created') }}</span>
          <span class="text-foreground tabular-nums">{{ formatDateTime(lead.created_at) }}</span>
        </div>
        <div v-if="lead.rule" class="flex min-w-0 flex-col gap-1">
          <span class="text-xs font-semibold tracking-wide text-muted-foreground uppercase">{{ tr('Automation') }}</span>
          <router-link :to="'/automation/' + lead.rule.id" class="truncate text-primary no-underline hover:underline">
            {{ lead.rule.name }}
          </router-link>
        </div>
      </div>

      <dl v-if="answers.length !== 0" class="m-0 flex flex-col gap-3">
        <div v-for="answer in answers" :key="answer.name" class="flex flex-col gap-0.5">
          <dt class="text-xs font-semibold text-muted-foreground">{{ answer.label }}</dt>
          <dd class="m-0 text-foreground [overflow-wrap:anywhere]">
            <lead-field-value :lead="lead" :field="answer.name" />
          </dd>
        </div>
      </dl>

      <div v-if="canManage" class="flex flex-col gap-2 border-t border-border/60 pt-5">
        <ui-label for="lead-comment">{{ tr('Add a note') }}</ui-label>
        <ui-textarea
          id="lead-comment"
          v-model="comment"
          rows="3"
          :maxlength="COMMENT_MAX_LENGTH"
          :placeholder="tr('E.g. Called, will call back tomorrow')"
        />
        <ui-button class="self-start" :disabled="isSavingComment || comment.trim() === ''" @click="() => saveComment()">
          <loader-circle-icon v-if="isSavingComment" class="animate-spin" />
          {{ tr('Save note') }}
        </ui-button>
      </div>

      <div class="flex flex-col gap-3 border-t border-border/60 pt-5">
        <span class="text-base font-semibold text-foreground">{{ tr('Activity history') }}</span>
        <lead-activity-timeline :activities="lead.activities" :created-at="lead.created_at" />
      </div>
    </template>

    <div v-else class="flex flex-col gap-3">
      <ui-skeleton class="h-28 w-full rounded-xl" />
      <ui-skeleton class="h-24 w-full rounded-xl" />
      <ui-skeleton class="h-4 w-40" />
      <ui-skeleton class="h-4 w-56" />
    </div>
  </ui-modal-layout>
</template>

<script>
import {
  Copy,
  LoaderCircle,
  Mail,
  Phone,
  UserRound,
} from '@lucide/vue';
import { markRaw } from 'vue';

import CompanyMemberListMixin from '@/mixins/CompanyMemberList.js';
import LeadgenFormLeadService from '@/services/LeadgenFormLead.js';
import LeadActivityTimeline from '@/ui/components/LeadActivityTimeline.vue';
import LeadAssigneeSelect from '@/ui/components/LeadAssigneeSelect.vue';
import LeadFieldValue from '@/ui/components/LeadFieldValue.vue';
import LeadStatusSelect from '@/ui/components/LeadStatusSelect.vue';
import { formatTime, formatToYMD } from '@/utils/date.js';

// Backend LeadgenFormLeadCrmService::COMMENT_MAX_LENGTH
const COMMENT_MAX_LENGTH = 5000;

export default {
  name: 'LeadgenFormLeadModalContent',
  components: {
    CopyIcon: Copy,
    LeadActivityTimeline,
    LeadAssigneeSelect,
    LeadFieldValue,
    LeadStatusSelect,
    LoaderCircleIcon: LoaderCircle,
  },
  mixins: [ CompanyMemberListMixin ],
  props: {
    leadId: {
      type: String,
      required: true,
    },
  },
  emits: [ 'close', 'updated' ],
  data: () => ({
    UserRoundIcon: markRaw(UserRound),
    COMMENT_MAX_LENGTH,
    lead: null,
    savingField: null,
    comment: '',
    isSavingComment: false,
  }),
  computed: {
    canManage() {
      return this.$root.loggedInUser?.level !== 'restricted_viewer';
    },
    contacts() {
      const contacts = [];
      if (this.lead.phone) {
        contacts.push({
          type: 'phone',
          icon: markRaw(Phone),
          label: this.tr('Phone number'),
          value: this.lead.phone,
          href: 'tel:' + this.lead.phone,
          action: this.tr('Call now'),
        });
      }
      if (this.lead.email) {
        contacts.push({
          type: 'email',
          icon: markRaw(Mail),
          label: this.tr('Email address'),
          value: this.lead.email,
          href: 'mailto:' + this.lead.email,
          action: this.tr('Write email'),
        });
      }

      return contacts;
    },
    answers() {
      return (this.lead.field_data ?? []).map(field => ({
        name: field.name,
        label: this.formatFieldName(field.name ?? ''),
      }));
    },
  },
  watch: {
    leadId: {
      immediate: true,
      handler() {
        this.loadLead();
      },
    },
  },
  methods: {
    formatDateTime(date) {
      return formatToYMD(date) + ' ' + formatTime(date);
    },
    formatFieldName(fieldName) {
      // Convert "car_make" to "Car Make"
      return fieldName
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },
    copy(text) {
      navigator.clipboard.writeText(text);
      this.$toast.clear();
      this.$toast.success(this.tr('Copied to clipboard.'));
    },
    loadLead() {
      this.lead = null;

      LeadgenFormLeadService.getById(this.leadId)
        .then(lead => this.lead = lead)
        .catch(err => {
          this.error(err);
          this.$emit('close');
        });
    },
    applyUpdate(request, field) {
      this.savingField = field;

      return request()
        .then(lead => {
          this.lead = lead;
          this.$emit('updated', lead);

          return lead;
        })
        .catch(err => this.error(err))
        .finally(() => this.savingField = null);
    },
    changeStatus(status) {
      this.applyUpdate(() => LeadgenFormLeadService.updateStatus(this.lead.id, status), 'status');
    },
    changeAssignee(userId) {
      this.applyUpdate(() => LeadgenFormLeadService.updateAssignee(this.lead.id, userId), 'assignee');
    },
    saveComment() {
      const text = this.comment.trim();
      if (!text || this.isSavingComment) {
        return;
      }

      this.isSavingComment = true;

      LeadgenFormLeadService.addComment(this.lead.id, text)
        .then(lead => {
          this.lead = lead;
          this.comment = '';

          return lead;
        })
        .catch(err => this.error(err))
        .finally(() => this.isSavingComment = false);
    },
  },
};
</script>
